import { Box, Typography } from "@mui/material";
import DocumentImageViewport from "./DocumentImageViewport";
import { APP_UI } from "../ui/appUi";

export interface DocumentComparisonItem {
  key: string;
  label: string;
  src?: string | null;
  alt: string;
  imageBackground?: string;
  emptyText?: string;
  warning?: boolean;
}

/**
 * Gemeinsame, scrollfreie Dokumentdarstellung für Einzelansicht und Vergleich.
 *
 * - Ein Dokument nutzt den gesamten verfügbaren Dialog-Viewport.
 * - Zwei Dokumente liegen auf breiten/landscape Viewports nebeneinander.
 * - Auf schmalen Portrait-Viewports teilen sie die verfügbare Höhe untereinander.
 * - Jeder Pane berücksichtigt seine eigene Überschrift; Bilder dürfen niemals
 *   über den verbleibenden Pane-Bereich hinausragen.
 */
export default function DocumentComparisonViewport({ items }: { items: DocumentComparisonItem[] }) {
  const visibleItems = items.slice(0, 2);
  const compare = visibleItems.length > 1;

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        minWidth: 0,
        minHeight: 0,
        overflow: "hidden",
        display: "grid",
        gridTemplateColumns: "minmax(0,1fr)",
        gridTemplateRows: compare ? "repeat(2,minmax(0,1fr))" : "minmax(0,1fr)",
        bgcolor: APP_UI.background,
        ...(compare
          ? {
              "@media (orientation: landscape)": {
                gridTemplateRows: "minmax(0,1fr)",
                gridTemplateColumns: "repeat(2,minmax(0,1fr))",
              },
              "@media (min-width:900px)": {
                gridTemplateRows: "minmax(0,1fr)",
                gridTemplateColumns: "repeat(2,minmax(0,1fr))",
              },
            }
          : {}),
      }}
    >
      {visibleItems.map((item, index) => (
        <Box
          key={item.key}
          sx={{
            minWidth: 0,
            minHeight: 0,
            overflow: "hidden",
            display: "grid",
            gridTemplateRows: "auto minmax(0,1fr)",
            borderBottom: compare && index === 0 ? `1px solid ${APP_UI.border}` : 0,
            ...(compare
              ? {
                  "@media (orientation: landscape)": {
                    borderBottom: 0,
                    borderRight: index === 0 ? `1px solid ${APP_UI.border}` : 0,
                  },
                  "@media (min-width:900px)": {
                    borderBottom: 0,
                    borderRight: index === 0 ? `1px solid ${APP_UI.border}` : 0,
                  },
                }
              : {}),
          }}
        >
          <Typography
            variant="caption"
            sx={{
              px: 1,
              py: .55,
              color: item.warning ? APP_UI.warning : APP_UI.accent,
              fontWeight: 850,
              bgcolor: APP_UI.surfaceSolid,
              lineHeight: 1.25,
            }}
          >
            {item.label}
          </Typography>
          {item.src ? (
            <DocumentImageViewport
              src={item.src}
              alt={item.alt}
              maxHeight="100%"
              imageBackground={item.imageBackground}
              sx={{ height: "100%", minHeight: 0 }}
            />
          ) : (
            <Box
              sx={{
                minWidth: 0,
                minHeight: 0,
                display: "grid",
                placeItems: "center",
                overflow: "hidden",
                p: 2,
                color: item.warning ? APP_UI.warning : APP_UI.textMuted,
                textAlign: "center",
              }}
            >
              <Typography variant="body2">{item.emptyText ?? "Keine Vorschau verfügbar."}</Typography>
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
}
